/* ============================================================
   Dathent — Edit Strategy
   Opens from Content Plan ("Edit strategy" button).
   Ported from design pack pages/edit-strategy.jsx → ES module
   with real API wiring (no demo data).
   ============================================================ */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons } from '../components/pack-icons/brands';
import { useActivity } from '../components/AppShell';

/* ---------------- Option lists ---------------- */
const ES_OFFER_OPTIONS = [
  { v: 'one',    label: 'A single product or collection', sub: 'e.g. winter dinnerware line' },
  { v: 'rec',    label: 'Recurring service or subscription', sub: 'e.g. monthly maintenance plan' },
  { v: 'awar',   label: 'Awareness / portfolio', sub: 'no specific offer — just brand presence' },
  { v: 'launch', label: 'A specific launch with a date', sub: 'e.g. event, drop, opening' },
];

const ES_TONE_OPTIONS = [
  { v: 'warm',      label: 'Warm & personable', sub: 'like a thoughtful friend' },
  { v: 'expert',    label: 'Expert & precise', sub: 'deep authority on craft' },
  { v: 'playful',   label: 'Playful & witty', sub: 'a smile in every post' },
  { v: 'bold',      label: 'Bold & confident', sub: 'strong opinions, sharp claims' },
  { v: 'editorial', label: 'Editorial & quiet', sub: 'slow, magazine-like cadence' },
  { v: 'minimal',   label: 'Minimal & direct', sub: 'few words, no fluff' },
];

const ES_PALETTES = [
  { v: 'natural',   label: 'Natural & textural',   palette: ['#A89A82', '#D9CBB3', '#766858', '#F3EBDD'] },
  { v: 'editorial', label: 'Editorial monochrome', palette: ['#111111', '#F4F4F4', '#777777', '#E8E0D5'] },
  { v: 'studio',    label: 'Studio pastel',        palette: ['#E8C2D8', '#C4D8E8', '#F2E8B5', '#FFFFFF'] },
  { v: 'electric',  label: 'Electric brand',       palette: ['#6E5BFF', '#4FC3FF', '#E455CF', '#0E0E10'] },
  { v: 'matte',     label: 'Matte & moody',        palette: ['#1F2128', '#3A3F4A', '#8A8E97', '#0E1014'] },
  { v: 'sun',       label: 'Sun-warmed',           palette: ['#F5B544', '#C45D2B', '#F0E5D2', '#3B2A1B'] },
];

const ES_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'uk', name: 'Українська' },
  { code: 'ru', name: 'Русский' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'pt', name: 'Português' },
];

const ES_PLATFORMS = [
  { id: 'instagram', name: 'Instagram' },
  { id: 'facebook',  name: 'Facebook' },
  { id: 'linkedin',  name: 'LinkedIn' },
  { id: 'x',         name: 'X' },
  { id: 'tiktok',    name: 'TikTok' },
  { id: 'telegram',  name: 'Telegram' },
  { id: 'threads',   name: 'Threads' },
];

/* ---------------- Helpers ---------------- */
const PERIOD_TO_WEEKS = { week: 1, month: 4.3, quarter: 13 };

function postsPerPeriodToPerWeek(posts, period) {
  const div = PERIOD_TO_WEEKS[period] || 4.3;
  return Math.max(1, Math.min(21, Math.round((posts || 0) / div)));
}

function perWeekToPostsPeriod(perWeek) {
  // Default landing zone: monthly view
  const perMonth = Math.round((perWeek || 3) * 4.3);
  return { posts: perMonth, period: 'month' };
}

function parseTones(brand) {
  // toneOfVoice is single string; customToneDescription may carry the rest as CSV.
  const set = new Set();
  if (brand?.toneOfVoice) set.add(String(brand.toneOfVoice).toLowerCase());
  const custom = brand?.customToneDescription;
  if (typeof custom === 'string' && custom.includes(',')) {
    custom.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean).forEach((t) => set.add(t));
  }
  // Filter to known options only
  const known = new Set(ES_TONE_OPTIONS.map((o) => o.v));
  const list = Array.from(set).filter((t) => known.has(t)).slice(0, 3);
  return list.length ? list : ['warm'];
}

function parseLanguages(brand) {
  const primary = brand?.language || 'en';
  const list = [primary];
  // sourceMetadata may carry additional languages from onboarding
  const meta = brand?.sourceMetadata;
  if (meta && Array.isArray(meta.languages)) {
    meta.languages.forEach((l) => {
      if (l && !list.includes(l)) list.push(l);
    });
  }
  return list.filter((l) => ES_LANGUAGES.some((opt) => opt.code === l));
}

function parsePalette(brand) {
  // visualStyle is a backend enum; we try to map to our palette IDs by string match.
  const style = String(brand?.visualStyle || '').toLowerCase();
  const direct = ES_PALETTES.find((p) => style === p.v);
  if (direct) return direct.v;
  if (style.includes('mono') || style.includes('editorial')) return 'editorial';
  if (style.includes('studio') || style.includes('pastel')) return 'studio';
  if (style.includes('electric') || style.includes('vivid')) return 'electric';
  if (style.includes('matte') || style.includes('dark') || style.includes('moody')) return 'matte';
  if (style.includes('sun') || style.includes('warm')) return 'sun';
  return 'natural';
}

function parseOffer(brand) {
  const offer = String(brand?.offer || '').toLowerCase();
  if (ES_OFFER_OPTIONS.some((o) => o.v === offer)) return offer;
  if (offer.includes('launch')) return 'launch';
  if (offer.includes('subscription') || offer.includes('recurring')) return 'rec';
  if (offer.includes('portfolio') || offer.includes('awareness')) return 'awar';
  return 'one';
}

function buildInitial(brand, prefs, accounts) {
  const connectedSet = new Set(
    (accounts || [])
      .map((a) => String(a?.platform || '').toLowerCase())
      .filter(Boolean)
  );
  const preferred = Array.isArray(brand?.preferredPlatforms) && brand.preferredPlatforms.length
    ? brand.preferredPlatforms
    : Array.from(connectedSet);

  return {
    business: brand?.businessDescription || '',
    offer: parseOffer(brand),
    audience: brand?.targetAudience || '',
    goal: Array.isArray(brand?.goals) && brand.goals.length ? brand.goals[0] : '',
    positioning: brand?.positioning || '',
    tones: parseTones(brand),
    palette: parsePalette(brand),
    languages: parseLanguages(brand),
    cadence: perWeekToPostsPeriod(prefs?.postsPerWeek),
    channels: preferred.filter((p) => ES_PLATFORMS.some((opt) => opt.id === p)),
  };
}

function unpackResponse(res, key) {
  return res?.data?.[key] || res?.[key] || res?.data || res || null;
}

function errMessage(err) {
  if (!err) return 'Unknown error';
  return err?.body?.error?.message || err?.body?.message || err?.message || 'Unknown error';
}

/* ---------------- Scroll-spy ---------------- */
function useScrollSpy(ids, offset = 140) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const onScroll = () => {
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top < offset) {
          current = id;
        }
      }
      setActive(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [ids.join(','), offset]);
  return active;
}

/* ---------------- Section + Field ---------------- */
function Section({ id, eyebrow, title, sub, children }) {
  return (
    <section id={id} className="es-section card">
      <header className="es-section-h">
        <div className="eyebrow"><span>{eyebrow}</span></div>
        <h2>{title}</h2>
        {sub && <p>{sub}</p>}
      </header>
      <div className="es-section-body">{children}</div>
    </section>
  );
}

function Field({ label, hint, count, max, children, full }) {
  return (
    <div className={`es-field ${full ? 'is-full' : ''}`}>
      <div className="es-field-h">
        {label && <label className="es-field-l">{label}</label>}
        {(count != null && max != null) && (
          <span className={`es-field-count mono ${count > max ? 'is-over' : count > max * 0.85 ? 'is-warn' : ''}`}>
            {count} / {max}
          </span>
        )}
      </div>
      {children}
      {hint && <div className="es-field-hint">{hint}</div>}
    </div>
  );
}

/* ---------------- Top-level EditStrategy ---------------- */
export const EditStrategy = () => {
  const navigate = useNavigate();
  const { setActivity } = useActivity();
  const [initialData, setInitialData] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);

  // Load
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      api.profile.getBrandProfile(),
      api.profile.getPublishingPreferences(),
      api.socialAccounts.list(),
    ]).then(([bRes, pRes, aRes]) => {
      if (cancelled) return;
      const brand = bRes.status === 'fulfilled' ? unpackResponse(bRes.value, 'brandProfile') : null;
      const prefs = pRes.status === 'fulfilled' ? unpackResponse(pRes.value, 'publishingPreferences') : null;
      const accs = aRes.status === 'fulfilled'
        ? (aRes.value?.accounts || aRes.value?.data?.accounts || aRes.value?.data || [])
        : [];
      setAccounts(Array.isArray(accs) ? accs : []);
      const built = buildInitial(brand, prefs, accs);
      setInitialData(built);
      setData(built);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const update = (key) => (value) => setData((d) => ({ ...d, [key]: value }));
  const toggleIn = (key, value) => {
    setData((d) => {
      const arr = d[key] || [];
      const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
      return { ...d, [key]: next };
    });
  };

  const isDirty = useMemo(() => {
    if (!initialData || !data) return false;
    return JSON.stringify(data) !== JSON.stringify(initialData);
  }, [initialData, data]);

  // Scroll-spy nav
  const sectionIds = useMemo(
    () => ['business', 'audience', 'goal', 'voice', 'visual', 'language', 'cadence', 'channels'],
    []
  );
  const active = useScrollSpy(sectionIds, 140);
  const goTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const handleDiscard = () => setData(initialData);

  // Fields whose change should trigger a paid plan regeneration.
  // Language alone (and palette/UI choices) just update the brand profile —
  // no need to spend strategy credits on a re-slot.
  const STRATEGY_RELEVANT_FIELDS = ['business', 'audience', 'offer', 'goal', 'positioning', 'tones', 'channels', 'cadence'];

  const strategyRelevantChanged = () => {
    if (!initialData) return true; // first save: regenerate
    for (const key of STRATEGY_RELEVANT_FIELDS) {
      const a = JSON.stringify(initialData[key] ?? null);
      const b = JSON.stringify(data[key] ?? null);
      if (a !== b) return true;
    }
    return false;
  };

  const handleSave = async () => {
    if (!data || saving) return;
    setSaving(true);
    setError(null);
    const needsRegeneration = strategyRelevantChanged();
    setActivity({
      kind: 'planning',
      project: needsRegeneration ? 'Updating strategy' : 'Saving brand changes',
      step: needsRegeneration
        ? 'Rebalancing pillars and re-slotting posts…'
        : 'Updating brand profile…',
    });

    try {
      // 1. Brand profile
      const tones = (data.tones || []).slice(0, 3);
      const languages = (data.languages || []).slice();
      const brandPayload = {
        businessDescription: (data.business || '').trim(),
        targetAudience: (data.audience || '').trim(),
        offer: data.offer || 'one',
        goals: data.goal ? [data.goal.trim()] : ['visibility'],
        positioning: (data.positioning || '').trim() || null,
        toneOfVoice: tones[0] || 'professional',
        customToneDescription: tones.length > 1 ? tones.join(', ') : null,
        visualStyle: data.palette || 'natural',
        language: languages[0] || 'en',
        preferredPlatforms: (data.channels || []).slice(),
        sourceMetadata: { languages },
      };

      // 2. Publishing preferences (cadence)
      const ppw = postsPerPeriodToPerWeek(data.cadence?.posts, data.cadence?.period);
      const prefsPayload = { postsPerWeek: ppw };

      await Promise.all([
        api.profile.updateBrandProfile(brandPayload),
        api.profile.updatePublishingPreferences(prefsPayload),
      ]);

      // 3. Recalculate strategy ONLY when a strategy-relevant field changed.
      // Pure language / visual style edits don't re-slot the calendar, so we
      // skip the paid generation call — saves credits, removes the "requires
      // confirmation before spending credits" wall on no-op saves.
      if (needsRegeneration) {
        await api.strategy.generate({ confirmCreditSpend: true });
      }

      // Reset baseline to saved data
      setInitialData(data);
      setActivity({
        kind: 'done',
        message: needsRegeneration
          ? 'Strategy saved · plan recalculated'
          : 'Brand changes saved',
      });
    } catch (err) {
      setError(errMessage(err));
      setActivity(null);
    } finally {
      setSaving(false);
    }
  };

  const connectedSet = useMemo(() => {
    const set = new Set();
    accounts.forEach((a) => {
      const p = String(a?.platform || '').toLowerCase();
      if (p) set.add(p);
    });
    return set;
  }, [accounts]);

  // Char limits
  const businessMax = 400;
  const audienceMax = 400;
  const goalMax = 240;
  const positioningMax = 200;

  if (loading || !data) {
    return (
      <div className="es-root">
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          Loading strategy…
        </div>
      </div>
    );
  }

  return (
    <div className="es-root">
      {/* Top */}
      <div className="es-top">
        <div>
          <button
            className="es-back"
            type="button"
            onClick={() => navigate('/app/plan')}
          >
            <I.chevLeft size={14} /> Content Plan
          </button>
          <h1 className="es-h">Edit strategy</h1>
          <p className="es-sub">
            Everything we captured in onboarding lives here. Change anything and the plan recalculates.
          </p>
        </div>
        {isDirty && (
          <span className="pill pill-warning" style={{ alignSelf: 'center' }}>
            <span className="dot" />Unsaved changes
          </span>
        )}
      </div>

      <div className="es-grid">
        {/* Left — section nav */}
        <aside className="es-nav">
          <div className="es-nav-h">
            <span className="eyebrow"><span>Sections</span></span>
          </div>
          <nav>
            {[
              { id: 'business', label: 'Business',           icon: I.layers },
              { id: 'audience', label: 'Audience',           icon: I.users },
              { id: 'goal',     label: 'Goal & positioning', icon: I.target },
              { id: 'voice',    label: 'Voice & tone',       icon: I.message },
              { id: 'visual',   label: 'Visual',             icon: I.image },
              { id: 'language', label: 'Languages',          icon: I.globe },
              { id: 'cadence',  label: 'Cadence',            icon: I.calendar },
              { id: 'channels', label: 'Channels',           icon: I.share },
            ].map((s) => (
              <button
                key={s.id}
                type="button"
                className={`es-nav-link ${active === s.id ? 'is-active' : ''}`}
                onClick={() => goTo(s.id)}
              >
                <s.icon size={14} />
                <span>{s.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Right — sections */}
        <div className="es-content">
          {/* BUSINESS */}
          <Section
            id="business"
            eyebrow="Business"
            title="What you do."
            sub="Captured during onboarding. Used as base context for every post."
          >
            <Field
              label="Description"
              count={(data.business || '').length}
              max={businessMax}
              hint="One or two sentences. The clearer this is, the better every draft."
              full
            >
              <textarea
                className="es-textarea"
                rows={3}
                value={data.business || ''}
                onChange={(e) => update('business')(e.target.value)}
              />
            </Field>

            <Field label="What you're selling right now" full>
              <div className="es-choice">
                {ES_OFFER_OPTIONS.map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    className={`es-choice-card ${data.offer === o.v ? 'is-active' : ''}`}
                    onClick={() => update('offer')(o.v)}
                  >
                    <span className="es-choice-radio">
                      {data.offer === o.v && <I.check size={11} />}
                    </span>
                    <div>
                      <div className="es-choice-label">{o.label}</div>
                      <div className="es-choice-sub">{o.sub}</div>
                    </div>
                  </button>
                ))}
              </div>
            </Field>
          </Section>

          {/* AUDIENCE */}
          <Section
            id="audience"
            eyebrow="Audience"
            title="Who you're reaching."
            sub="Demographics plus what they care about. The 'why' is more useful than the 'who'."
          >
            <Field label="Your audience" count={(data.audience || '').length} max={audienceMax} full>
              <textarea
                className="es-textarea"
                rows={3}
                value={data.audience || ''}
                onChange={(e) => update('audience')(e.target.value)}
              />
            </Field>
          </Section>

          {/* GOAL & POSITIONING */}
          <Section
            id="goal"
            eyebrow="Goal & positioning"
            title="What you're trying to move."
            sub="The two lines that shape what every post is for."
          >
            <Field
              label="Goal"
              hint="One outcome you want this quarter."
              count={(data.goal || '').length}
              max={goalMax}
              full
            >
              <textarea
                className="es-textarea"
                rows={2}
                value={data.goal || ''}
                onChange={(e) => update('goal')(e.target.value)}
              />
            </Field>
            <Field
              label="Positioning"
              hint="The single sentence that explains where you sit in your category."
              count={(data.positioning || '').length}
              max={positioningMax}
              full
            >
              <textarea
                className="es-textarea"
                rows={2}
                value={data.positioning || ''}
                onChange={(e) => update('positioning')(e.target.value)}
              />
            </Field>
          </Section>

          {/* VOICE / TONE */}
          <Section
            id="voice"
            eyebrow="Voice"
            title="How your posts feel."
            sub="Pick up to 3 tones. We'll mix them as needed across the plan."
          >
            <Field full>
              <div className="es-choice">
                {ES_TONE_OPTIONS.map((o) => {
                  const on = (data.tones || []).includes(o.v);
                  const disabled = !on && (data.tones || []).length >= 3;
                  return (
                    <button
                      key={o.v}
                      type="button"
                      className={`es-choice-card ${on ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
                      onClick={() => !disabled && toggleIn('tones', o.v)}
                    >
                      <span className="es-choice-check">{on && <I.check size={11} />}</span>
                      <div>
                        <div className="es-choice-label">{o.label}</div>
                        <div className="es-choice-sub">{o.sub}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="es-meta-row">
                <span className="mono">{(data.tones || []).length} of 3 selected</span>
              </div>
            </Field>
          </Section>

          {/* VISUAL */}
          <Section
            id="visual"
            eyebrow="Visual"
            title="The look across imagery."
            sub="Used for image generation, video covers, and carousels."
          >
            <Field full>
              <div className="es-palette">
                {ES_PALETTES.map((p) => (
                  <button
                    key={p.v}
                    type="button"
                    className={`es-pal ${data.palette === p.v ? 'is-active' : ''}`}
                    onClick={() => update('palette')(p.v)}
                  >
                    <div className="es-pal-swatch">
                      <span style={{ background: p.palette[0] }} />
                      <span style={{ background: p.palette[1] }} />
                      <span style={{ background: p.palette[2] }} />
                      <span style={{ background: p.palette[3] }} />
                    </div>
                    <div className="es-pal-label">{p.label}</div>
                    {data.palette === p.v && (
                      <span className="es-pal-check">
                        <I.check size={11} />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </Field>
          </Section>

          {/* LANGUAGES — single global default. On Growth+ users can override
              per connected social account in Settings → Connected accounts. */}
          <Section
            id="language"
            eyebrow="Default publish language"
            title="What we write in by default."
            sub="We'll write and design every post in this language. On Growth+ you can override per connected account in Settings."
          >
            <Field full>
              <div className="es-langs">
                {ES_LANGUAGES.map((l) => {
                  // Single-select: primary is data.languages[0] (data may still
                  // carry the legacy list for backward compat — we only honour
                  // the first entry now).
                  const active = (data.languages?.[0] || 'en') === l.code;
                  return (
                    <button
                      key={l.code}
                      type="button"
                      className={`es-lang ${active ? 'is-active' : ''}`}
                      onClick={() => update('languages')([l.code])}
                    >
                      <span className="es-lang-code">{l.code.toUpperCase()}</span>
                      <span className="es-lang-name">{l.name}</span>
                      {active && <I.check size={12} className="es-lang-check" />}
                    </button>
                  );
                })}
              </div>
            </Field>
          </Section>

          {/* CADENCE */}
          <Section
            id="cadence"
            eyebrow="Cadence"
            title="How often we post."
            sub="The plan auto-distributes this volume across your channels and pillars."
          >
            <div className="es-cadence">
              <Field label="Number of posts">
                <div className="es-stepper">
                  <button
                    type="button"
                    onClick={() =>
                      update('cadence')({
                        ...data.cadence,
                        posts: Math.max(1, (data.cadence?.posts || 0) - 5),
                      })
                    }
                  >
                    −
                  </button>
                  <div className="es-stepper-v">
                    <span className="es-stepper-num">{data.cadence?.posts || 0}</span>
                    <span className="es-stepper-unit">posts</span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      update('cadence')({
                        ...data.cadence,
                        posts: Math.min(120, (data.cadence?.posts || 0) + 5),
                      })
                    }
                  >
                    +
                  </button>
                </div>
                <div className="es-stepper-presets">
                  {[7, 14, 30, 60].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={data.cadence?.posts === n ? 'is-active' : ''}
                      onClick={() => update('cadence')({ ...data.cadence, posts: n })}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Per">
                <div className="es-period">
                  {[
                    { v: 'week',    l: 'Week' },
                    { v: 'month',   l: 'Month' },
                    { v: 'quarter', l: 'Quarter' },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      className={`es-period-btn ${data.cadence?.period === o.v ? 'is-active' : ''}`}
                      onClick={() => update('cadence')({ ...data.cadence, period: o.v })}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
                <div className="es-cadence-calc">
                  ≈ <b>
                    {Math.round(
                      ((data.cadence?.posts || 0) /
                        (PERIOD_TO_WEEKS[data.cadence?.period] || 4.3)) *
                        10
                    ) / 10}
                  </b> posts / week
                </div>
              </Field>
            </div>
          </Section>

          {/* CHANNELS */}
          <Section
            id="channels"
            eyebrow="Channels"
            title="Where it goes."
            sub="Toggle a channel to include or exclude it from the plan."
          >
            <Field full>
              <div className="es-channels">
                {ES_PLATFORMS.map((p) => {
                  const connected = connectedSet.has(p.id);
                  const on = connected && (data.channels || []).includes(p.id);
                  const sub = connected
                    ? accounts
                        .filter((a) => String(a.platform || '').toLowerCase() === p.id)
                        .map((a) => a.handle || a.accountName || a.displayName || a.username)
                        .filter(Boolean)
                        .join(', ') || 'Connected'
                    : 'Not connected';
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className={`es-channel ${on ? 'is-on' : ''} ${!connected ? 'is-disabled' : ''}`}
                      onClick={() => connected && toggleIn('channels', p.id)}
                      disabled={!connected}
                    >
                      <span
                        className={`ob-net-ic-${p.id}`}
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 6,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        {BrandIcons[p.id]?.({ size: 15 })}
                      </span>
                      <div className="es-channel-text">
                        <div className="es-channel-name">{p.name}</div>
                        <div className="es-channel-sub">{sub}</div>
                      </div>
                      <span className={`es-channel-toggle ${on ? 'is-on' : ''}`}>
                        {on && <I.check size={11} />}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                className="es-channel-link"
                onClick={() => navigate('/app/settings?tab=accounts')}
                style={{ background: 'none', border: 0, cursor: 'pointer' }}
              >
                <I.plus size={12} /> Connect a new channel
              </button>
            </Field>
          </Section>

          {error && (
            <div className="card" style={{ padding: 12, color: 'var(--danger-text)', border: '1px solid var(--danger)' }}>
              <I.alert size={14} /> {error}
            </div>
          )}

          {/* Spacer for sticky footer */}
          <div style={{ height: 24 }} />
        </div>
      </div>

      {/* Sticky save bar */}
      <div className={`es-save-bar ${isDirty ? 'is-dirty' : ''}`}>
        <div className="es-save-inner">
          <div className="es-save-text">
            {isDirty ? (
              <>
                <span className="dot pulse" style={{ background: 'var(--warning)' }} />
                <span>
                  {strategyRelevantChanged()
                    ? 'You have unsaved changes. Saving will recalculate the plan and re-slot upcoming posts.'
                    : 'You have unsaved brand changes. Saving applies them — your plan stays as-is.'}
                </span>
              </>
            ) : (
              <>
                <I.check size={13} style={{ color: 'var(--success)' }} />
                <span>Strategy is up to date. No changes to save.</span>
              </>
            )}
          </div>
          <div className="es-save-actions">
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              disabled={!isDirty || saving}
              onClick={handleDiscard}
            >
              Discard
            </button>
            <button
              className="btn btn-primary btn-sm"
              type="button"
              disabled={!isDirty || saving}
              onClick={handleSave}
            >
              {saving ? (
                <>
                  <I.refresh size={12} /> Saving…
                </>
              ) : (
                <>
                  <I.check size={12} />{' '}
                  {strategyRelevantChanged() ? 'Save & recalculate' : 'Save changes'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditStrategy;
